import { Text } from "@merge-api/merge-javascript-shared";
import { ChevronRight, GitBranch } from "lucide-react";
import MergeTable from "../../shared/MergeTable";
import TableSkeletonLoader from "../../shared/TableSkeletonLoader";
import IntegrationBuilderEmptyState from "../../integration-builder/shared/IntegrationBuilderEmptyState";
import { PlanSummary } from "../types";
import { formatRelativeTime } from "../helpers";
import { EnabledBadge, ModeBadge } from "./ConductorBadges";

// NOTE: the design's "Used by" column (how many playbook entries / plan steps
// reference a plan) needs a conductor GetPlanReferences RPC that doesn't exist
// yet. It's omitted here and added once that endpoint lands.
const NUM_COLUMNS = 8;

interface Props {
  plans: PlanSummary[];
  isLoading: boolean;
  onOpen: (planId: string) => void;
}

const ConductorPlansTable = ({ plans, isLoading, onOpen }: Props) => (
  <MergeTable
    hover={false}
    isOverviewPage
    className="border border-gray-10"
    header={
      <>
        <th scope="col">Plan ID</th>
        <th scope="col">Category</th>
        <th scope="col">Name</th>
        <th scope="col">Mode</th>
        <th scope="col">Steps</th>
        <th scope="col">Status</th>
        <th scope="col">Updated</th>
        <th scope="col" />
      </>
    }
    content={
      isLoading ? (
        <TableSkeletonLoader cols={NUM_COLUMNS} rows={8} />
      ) : plans.length === 0 ? (
        <IntegrationBuilderEmptyState
          title="No plans"
          subtitle="No plans for this integration yet."
          numberOfColumns={NUM_COLUMNS}
          withIcon
          icon={GitBranch}
        />
      ) : (
        plans.map((plan) => (
          <tr
            key={plan.id}
            onClick={() => onOpen(plan.id)}
            className="cursor-pointer hover:bg-[#F9FAFB]"
          >
            <td>
              <Text variant="md" className="font-mono">
                {plan.id}
              </Text>
            </td>
            <td>
              <Text variant="md" className="font-mono text-gray-60">
                {plan.category || <span className="text-gray-40">-</span>}
              </Text>
            </td>
            <td>
              <Text variant="md" className="font-medium">
                {plan.name}
              </Text>
            </td>
            <td>
              <ModeBadge mode={plan.mode} />
            </td>
            <td>
              <Text variant="md" className="font-mono">
                {plan.step_count}
              </Text>
            </td>
            <td>
              <EnabledBadge enabled={plan.enabled} />
            </td>
            <td>
              <Text variant="md">{formatRelativeTime(plan.modified_at)}</Text>
            </td>
            <td className="text-gray-60">
              <ChevronRight size={16} />
            </td>
          </tr>
        ))
      )
    }
  />
);

export default ConductorPlansTable;
